/**
 * HumanTaskGuardSection - Travas no registro enquanto a pendência estiver aberta
 *
 * Responde a uma pergunta DIFERENTE do campo "O processo aguarda esta etapa?"
 * (que decide se o ramo do processo fica parado). Aqui se configura o que o
 * usuário pode ou não fazer no registro (negócio) fora do processo enquanto a
 * pendência estiver pendente. Quem lê esses campos é a RPC
 * `workflow_guard_check`, através de `workflow_human_tasks.enforcement` e
 * `workflow_human_tasks.guard_actions` (propagados por `workflow_open_human_task`).
 *
 * Importante: o motor só considera pendências BLOQUEANTES. Por isso a seção
 * fica desabilitada quando o processo não aguarda a etapa.
 *
 * Contrato gravado em `workflow_nodes.config`:
 *   { enforcement: 'ENFORCE' | 'WARN' | 'OBSERVE' | 'NONE', guard_actions: string[] }
 *
 * @version 1.1.0
 * RELEASE: 2026-09-01 v1.1.0 - Deixa explícito que a trava é gravada na pendência
 *          e que lista de ações vazia trava todas as ações protegidas.
 * RELEASE: 2026-09-01 v1.0.0 - Criação inicial: separa a trava do registro do
 *          comportamento de espera do processo e expõe as ações travadas.

 */

import { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InlineNotice } from '@/components/ui/inline-notice';
import { SectionCard, CompactLabel } from './SectionCard';
import { ListaBuscavel } from './ExigenciaEditor';
import { ROTULO_MODULO, useWorkflowGuardActions } from '@/hooks/useWorkflowGuardActions';

interface Props {
  config: Record<string, any>;
  setCfg: (chave: string, valor: unknown) => void;
}

export function HumanTaskGuardSection({ config, setCfg }: Props) {
  const { data: catalogoAcoes = [] } = useWorkflowGuardActions();

  // A trava só vale para pendências que param o processo (regra do motor)
  const aguarda = String(config.mode || 'bloqueante') !== 'nao_bloqueante';
  const acoes: string[] = Array.isArray(config.guard_actions) ? config.guard_actions : [];

  const alternarAcao = (code: string) => {
    const atual = new Set(acoes);
    if (atual.has(code)) atual.delete(code);
    else atual.add(code);
    setCfg('guard_actions', Array.from(atual));
  };

  const grupos = useMemo(() => {
    const mapa = new Map<string, Array<{ code: string; label: string }>>();
    catalogoAcoes.forEach((a) => {
      const chave = ROTULO_MODULO[a.module] ?? a.module;
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave)!.push({ code: a.code, label: a.label });
    });
    return Array.from(mapa.entries()).map(([titulo, itens]) => ({ titulo, itens }));
  }, [catalogoAcoes]);

  const rotuloAcao = (code: string) => catalogoAcoes.find((a) => a.code === code)?.label ?? code;

  return (
    <SectionCard title="Travas no registro enquanto pendente">
      {!aguarda && (
        <InlineNotice variant="warning">
          O processo segue em paralelo nesta etapa, então nenhuma trava é aplicada. Escolha "Sim — o processo
          para até a resposta" em Execução para usar as travas.
        </InlineNotice>
      )}

      <div className={aguarda ? 'space-y-3' : 'pointer-events-none space-y-3 opacity-50'}>
        <div className="space-y-1.5">
          <CompactLabel help="Vale só para ações feitas fora do processo, enquanto a pendência estiver aberta.">
            Trava
          </CompactLabel>
          <Select value={String(config.enforcement || 'ENFORCE')} onValueChange={(v) => setCfg('enforcement', v)}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ENFORCE">Bloquear a ação</SelectItem>
              <SelectItem value="WARN">Apenas avisar</SelectItem>
              <SelectItem value="OBSERVE">Somente registrar</SelectItem>
              <SelectItem value="NONE">Sem trava</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {String(config.enforcement || 'ENFORCE') !== 'NONE' && (
          <>
            <ListaBuscavel
              titulo="Ações travadas"
              ajuda="Lista vinda do catálogo do sistema. Deixe vazio para valer em todas as ações protegidas."
              placeholder="Buscar ação"
              selecionados={acoes}
              rotuloDe={rotuloAcao}
              onAlternar={alternarAcao}
              grupos={grupos}
            />
            {acoes.length === 0 && (
              <InlineNotice variant="info">
                Nenhuma ação marcada: a trava vale para todas as ações protegidas do registro.
              </InlineNotice>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}

